/**
 * The profile editor's own header and card-frame catalogue (`136:950`): the
 * usage sentence, the name field and the media cards' periods. A period's colour is
 * derived from its frequency (`periods.ts`'s `periodSwatch`/`defaultHue`) until a swatch is
 * picked for it.
 *
 * `accounts.*` is the "Quem usa este perfil" rail and its overflow modal: a
 * card carrying its own count badge, a real error/empty/loading surface for
 * its own fetch, and the modal's column headers, caption and pagination
 * labels -- distinct from `usage.*` above, which reads the listing route's
 * own `usage_count`.
 */

import { defineCatalogue } from '../../../../i18n';

export const profileCatalogue = defineCatalogue(
  {
    discard: 'Descartar',
    'select.label': 'Selecionar perfil',
    'select.title': 'Escolha um perfil para ver',
    'select.search': 'Buscar perfil de coleta',
    'select.none': 'Nenhum perfil encontrado',
    'save.open': 'Rever e aplicar',
    'usage.accounts.one': '{count} conta',
    'usage.accounts.other': '{count} contas',
    'usage.sentence': 'Usado por {accounts}.',
    'media.title': 'Períodos de coleta',
    'media.empty': 'Este perfil não tem nenhum tipo de mídia configurado.',
    'kind.off': 'Sem coleta',
    'collect.comments': 'Coletar comentários',
    'collect.likers': 'Ler curtidas junto com os comentários',
    'period.summary': 'A cada {frequency}, por {duration}',
    'period.summary.frequencyOnly': 'A cada {frequency}',
    'period.title': 'Período {n}',
    'period.range': 'de {start} até {end} após a postagem',
    'period.start': 'a postagem',
    'period.frequency': 'Frequência',
    'period.duration': 'Duração',
    'period.add': 'Adicionar período',
    'period.remove': 'Remover período {n}',
    'period.remove.only': 'O único período não pode ser removido.',
    'period.transition':
      'A duração não é múltipla da frequência: a transição entre períodos pode ficar mais curta que o configurado.',
    'period.boundary': 'Fim do período {n}',
    'period.segment': 'Período {n}: a cada {frequency} por {duration}',
    'period.timeline': 'Linha do tempo dos períodos',
    'period.day': '{n}d',
    'period.total': 'Coleta por {total} após a postagem',
    'story.frequency': 'Frequência dos stories',
    'audio.pageLimit.label': 'Páginas por leitura',
    'audio.pageLimit.toEnd': 'Até o fim da lista',
    'name.label': 'Nome do perfil',
    'name.required': 'Dê um nome ao perfil.',
    'name.taken': 'Já existe um perfil com esse nome nesta organização.',
    'color.label': 'Cor do período {n}',
    'color.auto': 'Usar a cor da frequência',
    'color.hue.red': 'vermelho',
    'color.hue.orange': 'laranja',
    'color.hue.yellow': 'amarelo',
    'color.hue.green': 'verde',
    'color.hue.blue': 'azul',
    'color.hue.purple': 'roxo',
    'color.hue.gray': 'cinza',
    'color.shade.pastel': 'pastel',
    'color.shade.normal': 'normal',
    'color.shade.dark': 'escuro',
    'color.swatch': '{hue} {shade}',
    'accounts.title': 'Quem usa este perfil',
    'accounts.count.one': '{count} conta',
    'accounts.count.other': '{count} contas',
    'accounts.overflow.one': 'e mais {count} conta',
    'accounts.overflow.other': 'e mais {count} contas',
    'accounts.empty': 'Nenhuma conta usa este perfil.',
    'accounts.handleNone': 'sem identificador',
    'accounts.modal.title': 'Contas que usam este perfil',
    'accounts.column.account': 'Conta',
    'accounts.column.campaign': 'Campanha',
    'accounts.table.caption': 'Contas que usam este perfil',
    'accounts.pagination.label': 'Paginação das contas',
    'accounts.pagination.previous': 'Anterior',
    'accounts.pagination.next': 'Próxima',
    'accounts.pagination.page': 'Página {page} de {pages}',
  },
  {
    discard: 'Discard',
    'select.label': 'Select profile',
    'select.title': 'Choose a profile to view',
    'select.search': 'Search collection profile',
    'select.none': 'No profile found',
    'save.open': 'Review and apply',
    'usage.accounts.one': '{count} account',
    'usage.accounts.other': '{count} accounts',
    'usage.sentence': 'Used by {accounts}.',
    'media.title': 'Collection periods',
    'media.empty': 'This profile has no media kind configured.',
    'kind.off': 'No collection',
    'collect.comments': 'Collect comments',
    'collect.likers': 'Read likes with the comments',
    'period.summary': 'Every {frequency}, for {duration}',
    'period.summary.frequencyOnly': 'Every {frequency}',
    'period.title': 'Period {n}',
    'period.range': 'from {start} to {end} after posting',
    'period.start': 'posting',
    'period.frequency': 'Frequency',
    'period.duration': 'Duration',
    'period.add': 'Add period',
    'period.remove': 'Remove period {n}',
    'period.remove.only': 'The only period cannot be removed.',
    'period.transition':
      'The duration is not a multiple of the frequency: the hand-over between periods may be shorter than configured.',
    'period.boundary': 'End of period {n}',
    'period.segment': 'Period {n}: every {frequency} for {duration}',
    'period.timeline': 'Period timeline',
    'period.day': '{n}d',
    'period.total': 'Collected for {total} after posting',
    'story.frequency': 'Stories frequency',
    'audio.pageLimit.label': 'Pages per lookup',
    'audio.pageLimit.toEnd': 'To the end of the list',
    'name.label': 'Profile name',
    'name.required': 'Give the profile a name.',
    'name.taken': 'This organisation already has a profile with that name.',
    'color.label': 'Period {n} colour',
    'color.auto': "Use the frequency's colour",
    'color.hue.red': 'red',
    'color.hue.orange': 'orange',
    'color.hue.yellow': 'yellow',
    'color.hue.green': 'green',
    'color.hue.blue': 'blue',
    'color.hue.purple': 'purple',
    'color.hue.gray': 'gray',
    'color.shade.pastel': 'pastel',
    'color.shade.normal': 'normal',
    'color.shade.dark': 'dark',
    'color.swatch': '{hue} {shade}',
    'accounts.title': 'Who uses this profile',
    'accounts.count.one': '{count} account',
    'accounts.count.other': '{count} accounts',
    'accounts.overflow.one': 'and {count} more account',
    'accounts.overflow.other': 'and {count} more accounts',
    'accounts.empty': 'No account uses this profile.',
    'accounts.handleNone': 'no handle',
    'accounts.modal.title': 'Accounts using this profile',
    'accounts.column.account': 'Account',
    'accounts.column.campaign': 'Campaign',
    'accounts.table.caption': 'Accounts using this profile',
    'accounts.pagination.label': 'Accounts pagination',
    'accounts.pagination.previous': 'Previous',
    'accounts.pagination.next': 'Next',
    'accounts.pagination.page': 'Page {page} of {pages}',
  },
);
